import type { BookableServiceRecord, OpenOrderRecord } from '@navio/nsdk';
import { type IncludedOrderLine, resolveIncludedOrderLines, resolveStationaryServicePolicy } from 'noddi-util';

type ResolveInput = {
  services: BookableServiceRecord[];
  lockedSalesItemId?: number | null;
  openOrder?: OpenOrderRecord | null;
  /** Sales items the proposal already prices itself. */
  proposalSalesItemIds: number[];
};

/**
 * Open-order items a stationary proposal's booking is charged for on top of the
 * proposal itself: the open order's residual lines, plus the service the open
 * order locked (that line is hidden from `openOrder.lines` and carries its
 * price instead).
 *
 * Only the locked service counts as an inclusion, never every non-editable one:
 * a catalog-locked service the customer simply cannot pick is not on this car's
 * open order and would inflate the total. A locked service the proposal already
 * prices is skipped for the same reason.
 */
export function resolveProposalIncludedOrderLines({
  services,
  lockedSalesItemId,
  openOrder,
  proposalSalesItemIds
}: ResolveInput): IncludedOrderLine[] {
  const { locked } = resolveStationaryServicePolicy(services, lockedSalesItemId ?? null);
  const isAlreadyOnProposal = locked != null && proposalSalesItemIds.includes(locked.salesItemId);

  return resolveIncludedOrderLines({
    fixedServices: locked && !isAlreadyOnProposal ? [locked] : [],
    openOrder
  });
}
